/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, September 2020
 */

import React, { useRef, useState, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Choice, Question } from '@staffino/model';
import { Box, convertCssUnit } from '@staffino/primitive-controls';
import { ScrollAnchor, useScrollEffect } from '../scroll';
import Header from '../header';
import { SingleChoiceControl } from '../controls';
import ChoiceTextInputControl from '../controls/choice-text-input-control';
import isEnterPressed from '../input-submit';
import asFix from '../components/as-fix.js';

type Props = {
  blockId: string;
  question: Question;
  choices: Choice[];
  otherChoice?: Choice;
  titleSize: Question['extraParams']['titleSize'];
  limit?: number;
  required?: boolean;
  colorStyle?: string;
} & React.ComponentProps<typeof Box>;

const ChoiceTextInputControlFix = asFix(ChoiceTextInputControl);

const ChoiceQuestionSingleSelectFragment = ({
  blockId,
  question,
  choices = [],
  otherChoice,
  titleSize,
  limit,
  required,
  colorStyle,
}: Props) => {
  const { t } = useTranslation();
  const context = useFormContext();

  const SingleChoiceControlComponent = context
    ? Controller
    : SingleChoiceControl;
  const ChoiceTextInputControlComponent = context
    ? Controller
    : ChoiceTextInputControl;

  const choiceValidation = { required };
  const selectedChoiceId = context?.watch(`${blockId}.choiceIds[0]`);
  const choiceError = context?.errors[blockId]?.choiceIds?.[0];

  const answerErrorMessage = useMemo(
    () => choiceError?.type === 'required' && t('Please answer this question.'),
    [choiceError, t],
  );

  const commentRef = useRef(null);
  const commentValue = context?.watch(`${blockId}.comment`);
  const [commentHighlighted, setCommentHighlighted] = useState(false);

  const [enterTriggered, setEnterTriggered] = useState(false);
  const { scrollToError: scrollToErrorOrdinary } = useScrollEffect(
    blockId,
    selectedChoiceId,
    1,
    selectedChoiceId !== otherChoice?.id ? 'scroll-down' : 'none',
  );
  const { scrollToError: scrollToErrorOther } = useScrollEffect(
    blockId,
    enterTriggered ? 'validValue' : null,
  );

  return (
    <>
      <ScrollAnchor id={blockId} />
      <Header
        question={question}
        size={titleSize}
        required={required}
        answerErrorMessage={answerErrorMessage}
      />
      <SingleChoiceControlComponent
        as={
          // there is an issue with passing onFocus directly in Controller in react-hook-form v5
          <SingleChoiceControl onFocus={() => scrollToErrorOrdinary(blockId)} />
        }
        name={`${blockId}.choiceIds[0]`}
        control={context?.control}
        rules={choiceValidation}
        choices={choices}
        otherChoice={otherChoice}
        colorStyle={colorStyle}
        commentHighlighted={commentHighlighted}
        onFocusOtherChoice={() => {
          if (!commentValue) {
            // even if it is not required, we need to encourage customer to fill it
            setCommentHighlighted(true);
            setTimeout(() => commentRef?.current?.focus());
          } else {
            scrollToErrorOther(blockId);
          }
        }}
        onBlurOtherChoice={() => {
          setCommentHighlighted(false);
          setTimeout(() => commentRef?.current?.blur());
        }}
        mt={titleSize === 'small' ? 0 : convertCssUnit('1.25rem')}
        otherChoiceInput={
          otherChoice && (
            <ChoiceTextInputControlComponent
              as={ChoiceTextInputControlFix}
              refFix={(element: any) => {
                context?.register?.(element);
                commentRef.current = element;
              }}
              onFocus={() => setCommentHighlighted(true)}
              onFocusFix={() => setCommentHighlighted(true)}
              name={`${blockId}.comment`}
              control={context?.control}
              placeholder={otherChoice.title || t('Other')}
              onKeyDown={(event) => {
                if (selectedChoiceId !== otherChoice.id) {
                  context?.setValue(`${blockId}.choiceIds[0]`, otherChoice.id);
                }
                setEnterTriggered(isEnterPressed(event));
                // if (isEnterPressed(event)) {
                //   setTimeout(() => commentRef?.current?.blur());
                // }
              }}
              onBlur={() => setCommentHighlighted(false)}
              maxLength={limit}
            />
          )
        }
      />
    </>
  );
};

export default ChoiceQuestionSingleSelectFragment;
